import * as THREE from 'three';
import { SparkRenderer, SparkViewpoint } from '@sparkjsdev/spark';

interface RearViewSparkModuleOptions {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  sparkRenderer: SparkRenderer;
}

export class RearViewSparkModule {
  private static readonly TARGET_WIDTH = 512;
  private static readonly TARGET_HEIGHT = 256;
  private static readonly TARGET_ASPECT =
    RearViewSparkModule.TARGET_WIDTH / RearViewSparkModule.TARGET_HEIGHT;

  private readonly scene: THREE.Scene;
  private readonly camera: THREE.PerspectiveCamera;
  private readonly sparkRenderer: SparkRenderer;
  private readonly rearCamera: THREE.PerspectiveCamera;
  private readonly viewpoint: SparkViewpoint;
  private readonly overlayAnchor: THREE.Group;
  private readonly overlayMaterial: THREE.MeshBasicMaterial;
  private readonly overlayMesh: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
  private readonly overlayDistance = 0.7;
  private readonly overlayHeightRatio = 0.3;
  private readonly overlayMarginRatio = 0.04;
  private readonly flipQuaternion = new THREE.Quaternion().setFromAxisAngle(
    new THREE.Vector3(0, 1, 0),
    Math.PI
  );
  private enabled = true;

  private hasTexture = false;
  private disposed = false;
  private lastLayoutFov = -1;
  private lastLayoutAspect = -1;

  constructor(options: RearViewSparkModuleOptions) {
    this.scene = options.scene;
    this.camera = options.camera;
    this.sparkRenderer = options.sparkRenderer;

    this.rearCamera = new THREE.PerspectiveCamera(
      this.camera.fov,
      RearViewSparkModule.TARGET_ASPECT,
      this.camera.near,
      this.camera.far
    );

    this.overlayAnchor = new THREE.Group();
    this.overlayAnchor.name = 'spark-rear-view-overlay';
    this.camera.add(this.overlayAnchor);

    const geometry = new THREE.PlaneGeometry(0.5, 0.5);
    this.overlayMaterial = new THREE.MeshBasicMaterial({
      map: SparkViewpoint.EMPTY_TEXTURE,
      depthWrite: false,
      depthTest: false,
      transparent: true,
      toneMapped: false,
    });
    this.overlayMesh = new THREE.Mesh(geometry, this.overlayMaterial);
    this.overlayMesh.renderOrder = 999;
    this.overlayAnchor.add(this.overlayMesh);
    this.overlayMesh.visible = false;

    this.viewpoint = new SparkViewpoint({
      spark: this.sparkRenderer,
      camera: this.rearCamera,
      autoUpdate: true,
      target: {
        width: RearViewSparkModule.TARGET_WIDTH,
        height: RearViewSparkModule.TARGET_HEIGHT,
      },
      onTextureUpdated: (texture) => {
        texture.needsUpdate = true;
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;
        this.overlayMaterial.map = texture;
        this.overlayMaterial.needsUpdate = true;
        this.hasTexture = true;
        this.overlayMesh.visible = true;
      },
    });
  }

  update(): void {
    if (this.disposed) {
      return;
    }
    if (!this.enabled) {
      this.overlayMesh.visible = false;
      return;
    }
    this.syncRearCamera();
    this.updateOverlayLayout();

    const shouldDisplay = this.hasTexture;
    this.overlayMesh.visible = false;
    this.viewpoint.renderTarget({
      scene: this.scene,
      camera: this.rearCamera,
    });
    this.overlayMesh.visible = shouldDisplay;
  }

  handleResize(): void {
    this.lastLayoutAspect = -1;
    this.lastLayoutFov = -1;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) {
      this.overlayMesh.visible = false;
    }
  }

  dispose(): void {
    if (this.disposed) {
      return;
    }
    this.disposed = true;

    // CRITICAL (iOS heap leak — heap snapshot proven):
    // RearViewSparkModule strongly holds scene + camera + sparkRenderer +
    // rearCamera + viewpoint + overlay*. Even after `dispose()`, these
    // `private readonly` fields are TS-only — in JS the refs persist on the
    // instance for as long as ANYTHING references the RearViewSparkModule
    // (in our case: the createScene IIFE closure scope, kept alive by
    // splatApi). This caused Scene / SparkRenderer / PerspectiveCamera to
    // appear in heap snapshot retainers as `RearViewSparkModule.scene/...`.
    //
    // Additionally, the SparkViewpoint constructor we passed our
    // `onTextureUpdated` callback to retains that callback, and the callback
    // closes over `this` — so SparkViewpoint → callback → this →
    // scene/camera/sparkRenderer is a second leak path (transit chain).
    //
    // Both must be broken explicitly.

    // 1) Sever SparkViewpoint → callback → this chain BEFORE viewpoint.dispose
    //    (so the closure can be GC'd as soon as the viewpoint releases the
    //    callback ref, which it doesn't do internally).
    const vp = this.viewpoint as unknown as {
      onTextureUpdated?: unknown;
      spark?: unknown;
      camera?: unknown;
    };
    vp.onTextureUpdated = undefined;

    this.viewpoint.dispose();
    this.overlayAnchor.remove(this.overlayMesh);
    this.overlayMesh.geometry.dispose();
    this.overlayMaterial.dispose();
    this.camera.remove(this.overlayAnchor);

    // 2) Sever SparkViewpoint's internal refs to spark + camera. Spark's
    //    own dispose doesn't null these.
    vp.spark = null;
    vp.camera = null;

    // 3) Null out OUR own retained refs (cast to bypass TS readonly — the
    //    instance itself will outlive dispose because the IIFE closure
    //    captures `rearViewModule` for as long as any other createScene
    //    closure is reachable).
    const self = this as unknown as Record<string, unknown>;
    self.scene = null;
    self.camera = null;
    self.sparkRenderer = null;
    self.rearCamera = null;
    self.viewpoint = null;
    self.overlayAnchor = null;
    self.overlayMaterial = null;
    self.overlayMesh = null;
  }

  private syncRearCamera(): void {
    this.rearCamera.position.copy(this.camera.position);
    this.rearCamera.quaternion.copy(this.camera.quaternion).multiply(this.flipQuaternion);
    this.rearCamera.fov = this.camera.fov;
    this.rearCamera.near = this.camera.near;
    this.rearCamera.far = this.camera.far;
    this.rearCamera.updateMatrixWorld(true);
    this.rearCamera.updateProjectionMatrix();
  }

  private updateOverlayLayout(): void {
    if (!this.overlayMesh) {
      return;
    }
    if (this.lastLayoutFov === this.camera.fov && this.lastLayoutAspect === this.camera.aspect) {
      return;
    }
    this.lastLayoutFov = this.camera.fov;
    this.lastLayoutAspect = this.camera.aspect;

    const distance = this.overlayDistance;
    const halfFovRad = THREE.MathUtils.degToRad(this.camera.fov * 0.5);
    const verticalSpan = 2 * distance * Math.tan(halfFovRad);
    const horizontalSpan = verticalSpan * this.camera.aspect;
    const overlayHeight = verticalSpan * this.overlayHeightRatio;
    const overlayWidth = overlayHeight * RearViewSparkModule.TARGET_ASPECT;
    const marginX = horizontalSpan * this.overlayMarginRatio;
    const marginY = verticalSpan * this.overlayMarginRatio;

    const x = horizontalSpan / 2 - overlayWidth / 2 - marginX;
    const y = -verticalSpan / 2 + overlayHeight / 2 + marginY;

    this.overlayMesh.position.set(x, y, -distance);
    this.overlayMesh.scale.set(overlayWidth, overlayHeight, 1);
  }
}
